import { useEffect, type ReactElement } from 'react';
import { Badge } from '@mantine/core';
import { Activity, FileCheck, ListChecks, ScanFace, UserCheck } from 'lucide-react';
import FeatureNavLayout from '@/shared/surfaces/FeatureNavLayout';
import type { FeatureNavGroup } from '@/shared/surfaces/FeatureNavTypes';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import { useGlobalFilterKey } from '@/features/global-filters/useGlobalFilters';
import { useAuth } from '@/features/auth/AuthContext';
import { GovernanceRegisterViewModel } from './GovernanceRegisterViewModel';
import { useGovernanceIssueStore } from './useGovernanceIssueStore';

const GOVERNANCE_BASE = '/governance';

/** Shared with the screens rendered in the outlet — the layout owns the register VM so its data also feeds the rail badges. */
export interface GovernanceOutletContext {
    registerVm: GovernanceRegisterViewModel;
}

/**
 * The Governance surface shell: a left rail of the primary screens — Policies,
 * Issues, Assigned to Me, Risk Density — then the Activity feed below a divider,
 * beside the scrollable content area. The register VM is owned here (not in a
 * screen) so its issue feed drives both the Issues/Assigned badges and the
 * Policies screen's register + summary rail; it's passed to the screens via the
 * outlet context.
 */
export default function GovernanceLayout(): ReactElement {
    const registerVm = useViewModel(GovernanceRegisterViewModel);
    useGovernanceIssueStore();
    const { user } = useAuth();
    const filterKey = useGlobalFilterKey();

    useEffect(() => {
        void registerVm.initialize();
    }, [registerVm, filterKey]);

    const openIssues = registerVm.openIssueCount;
    const assignedToMe = user?.email ? registerVm.assignedCount(user.email) : 0;

    const groups: FeatureNavGroup[] = [
        {
            selectGroupLabel: 'Governance',
            items: [
                { path: `${GOVERNANCE_BASE}/policies`, label: 'Policies', icon: FileCheck, matchPaths: [`${GOVERNANCE_BASE}/policy`] },
                {
                    path: `${GOVERNANCE_BASE}/issues`,
                    label: 'Issues',
                    icon: ListChecks,
                    rightSection: openIssues > 0 ? <Badge size="sm" variant="filled" color="red">{openIssues}</Badge> : undefined,
                },
                {
                    path: `${GOVERNANCE_BASE}/assigned`,
                    label: 'Assigned to Me',
                    icon: UserCheck,
                    rightSection: assignedToMe > 0 ? <Badge size="sm" variant="filled" color="blue">{assignedToMe}</Badge> : undefined,
                },
                { path: `${GOVERNANCE_BASE}/risk-density`, label: 'Risk Density', icon: ScanFace },
            ],
        },
        {
            dividerBefore: true,
            selectGroupLabel: 'Activity',
            items: [{ path: `${GOVERNANCE_BASE}/activity`, label: 'Activity', icon: Activity }],
        },
    ];

    const context: GovernanceOutletContext = { registerVm };

    return <FeatureNavLayout title="Governance" ariaLabel="Governance view" groups={groups} outletContext={context} />;
}
